import { DeleteOutline, Image } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { imgUrl, TOKEN_KEY, url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import DeleteConfirmation from 'src/Utils/DeleteConfirmation';
import CreateGallery from './CreateGallery';
import UpdateGallery from './UpdateGallery';

export default function AllGalleries() {
  const [isLoading, setIsLoading] = useState(true);
  const [galleries, setAllGalleries] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [galleryToUpdate, setGalleryToUpdate] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [imageDeleteModal, setimageDeleteModal] = useState(false);
  const [image, setImage] = useState('');

  const token = localStorage.getItem(TOKEN_KEY);
  const Toast = Toaster();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const handleOpen = () => setCreateModalOpen(true);
  const handleClose = () => setCreateModalOpen(false);

  // ================ Fetch Gallery ===================
  useEffect(() => {
    setIsLoading(true);
    (async function () {
      try {
        const uri = `${url}/api/v1/admin/gallery`;
        const { data } = await axios.get(uri, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });

        if (data.success) {
          setAllGalleries(data.data);
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    })();
  }, [refreshTrigger]);

  // =============== Delete Gallery ===================
  const handleDeleteGallery = async (id: number) => {
    try {
      setIsLoading(true);
      const data = await axios.delete(`${url}/api/v1/admin/gallery/${id}`, {
        data: { title: selectedGallery?.title },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (data.data.success) {
        setAllGalleries((prev) =>
          prev.filter((g) => g.title !== selectedGallery.title)
        );

        Toast.fire({
          icon: 'success',
          title: data.data.message || 'Gallery deleted successfully!',
        });
        closeDeleteModal();
      } else {
        Toast.fire({
          icon: 'error',
          title: data.data.message,
        });
      }
      setIsLoading(false);
    } catch (error: any) {
      Toast.fire({
        icon: 'error',
        title: error.data?.data?.message || 'Error deleting. Try again.',
      });
      setIsLoading(false);
    }
  };

  const openDeleteModal = (gallery) => {
    setSelectedGallery(gallery);
    setOpen(true);
  };

  const closeDeleteModal = () => {
    setOpen(false);
    setSelectedGallery(null);
  };

  // ================= Delete Single Image ==================
  const handleDeleteImage = async () => {
    try {
      setIsLoading(true);
      const response = await axios.delete(
        `${url}/api/v1/admin/gallery/img/${image}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        Toast.fire({ icon: 'success', title: 'Image deleted!' });
        setRefreshTrigger((prev) => prev + 1);
      } else {
        Toast.fire({ icon: 'error', title: response.data.message });
      }
      setimageDeleteModal(false);
      setIsLoading(false);
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: 'Error deleting image',
      });
      setIsLoading(false);
    }
  };

  const handleImage = (id) => {
    setImage(id);
    setimageDeleteModal(true);
  };

  // ================= Open First Gallery By Default ===================
  const [openGallery, setOpenGallery] = useState({});
  useEffect(() => {
    if (galleries.length > 0) {
      const initialState = {};
      galleries.forEach((_, index) => {
        initialState[index] = index === 0;
      });
      setOpenGallery(initialState);
    }
  }, [galleries]);

  const toggleGallery = (galleryIndex) => {
    setOpenGallery((prev) => ({
      ...prev,
      [galleryIndex]: !prev[galleryIndex],
    }));
  };

  // =============== Update Gallery ================
  const openUpdateModal = (gallery) => {
    setGalleryToUpdate(gallery);
    setUpdateModalOpen(true);
  };
  const closeUpdateModal = () => {
    setUpdateModalOpen(false);
    setGalleryToUpdate(null);
  };

  return (
    <Container maxWidth='xl'>
      <Card
        sx={{
          mb: 3,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0px 2px 6px rgba(0,0,0,0.06)',
        }}
      >
        <PageTitle heading='Gallery Manager' />

        <Button variant='contained' onClick={handleOpen}>
          Create Gallery
        </Button>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {galleries?.map((gallery, galleryIndex) => (
              <Card key={galleryIndex} sx={{ width: '100%' }}>
                {/* Header */}
                <CardHeader
                  title={
                    <Typography fontWeight='600'>{gallery.title}</Typography>
                  }
                  subheader={`${gallery.images?.length} images`}
                  action={
                    <Stack direction='row' spacing={1}>
                      <Button
                        variant='contained'
                        size='small'
                        onClick={() => toggleGallery(galleryIndex)}
                      >
                        {openGallery[galleryIndex] ? 'Hide' : 'View'}
                      </Button>

                      <Button
                        variant='outlined'
                        size='small'
                        onClick={() => openUpdateModal(gallery)}
                      >
                        Update
                      </Button>

                      <Button
                        variant='outlined'
                        color='error'
                        size='small'
                        onClick={() => openDeleteModal(gallery)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  }
                />

                {/* Gallery Images */}
                {openGallery[galleryIndex] && (
                  <CardContent sx={{ p: 2 }}>
                    <Grid container spacing={1.5}>
                      {gallery?.images?.map((image, i) => (
                        <Grid key={i} item xs={6} sm={4} md={3} lg={2} xl={2}>
                          <Box
                            sx={{
                              width: '100%',
                              aspectRatio: '1 / 1', // ❗ big fix here
                              borderRadius: 1,
                              overflow: 'hidden',
                              position: 'relative',
                              border: '1px solid',
                              borderColor: 'divider',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'grey.100',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
                              },
                            }}
                          >
                            <img
                              src={`${imgUrl}/${image.file}`}
                              alt=''
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover', // ❗ ensures full fill
                              }}
                            />

                            {/* Delete Icon */}
                            <IconButton
                              sx={{
                                position: 'absolute',
                                top: 6,
                                right: 6,
                                bgcolor: 'rgba(255,255,255,0.9)',
                                '&:hover': {
                                  bgcolor: 'error.light',
                                  color: 'white',
                                },
                              }}
                              onClick={() => handleImage(image.id)}
                            >
                              <DeleteOutline fontSize='small' color='error' />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                )}
              </Card>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Delete image modal */}
      <DeleteConfirmation
        open={imageDeleteModal}
        handleClose={() => setimageDeleteModal(false)}
        text='Delete this image?'
        handleDeleteFun={handleDeleteImage}
        loading={isLoading}
      />

      {/* Delete gallery modal */}
      <DeleteConfirmation
        open={open}
        handleClose={closeDeleteModal}
        text={`Delete "${selectedGallery?.title}" gallery?`}
        handleDeleteFun={() => handleDeleteGallery(selectedGallery?.id)}
        loading={isLoading}
      />

      {/* Create gallery modal */}
      <Dialog
        fullScreen={fullScreen}
        open={createModalOpen}
        onClose={handleClose}
        maxWidth='md'
      >
        <DialogContent>
          <Typography sx={{ mb: 1 }}>Create Gallery</Typography>
          <Divider sx={{ mb: 2 }} />

          <CreateGallery
            handleClose={handleClose}
            setRefreshTrigger={setRefreshTrigger}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Update gallery modal */}
      <Dialog
        fullScreen={fullScreen}
        open={updateModalOpen}
        onClose={closeUpdateModal}
        maxWidth='md'
      >
        <DialogContent>
          <Typography sx={{ mb: 1 }}>Update Gallery</Typography>
          <Divider sx={{ mb: 2 }} />

          {galleryToUpdate && (
            <UpdateGallery
              gallery={galleryToUpdate}
              handleClose={closeUpdateModal}
              setRefreshTrigger={setRefreshTrigger}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUpdateModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
